/**
 * Teczka przy zleceniu na Rynku (plan „VoltValue dla domu”, P5, 2026-10-24): czyste funkcje dla widoku wykonawcy
 * (`components/teczka/TeczkaWykonawcy`) i dla klienta (`DolaczTeczke`, przycisk w Projekcie 3D).
 *
 * Wejściem jest część projektu z teczki w kształcie, który widzi wykonawca (backend `teczka/prywatnosc.py`): bez
 * współrzędnych domu, obrysu z ewidencji, adresu i mapy. Rzut dachu składa się z samego zapisu projektu (`daneRzutu`),
 * więc wykonawca nie potrzebuje sceny 3D ani niczego, czego nie ma w widoku.
 */
import { daneRzutu } from '../projekt3d/rzut'
import { nazwaSprzetu } from '../projekt3d/klient3d'
import { daneInstalacjiZProjektu, opisRysunkowInstalacji } from '../rysunek/instalacja'
import { opisRysunkuDachu } from '../rysunek/rzutDachu'
import { ponumerujArkusze } from '../rysunek/arkusze'

const zl = (x) => `${Math.round(x / 10) * 10}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
const lata = (x) => `${String(Math.round(x * 10) / 10).replace('.', ',')}`
const zakresPl = (r, f) => (r.od != null && r.do != null && Math.round(r.od) !== Math.round(r.do) ? `${f(r.od)} do ${f(r.do)}` : f(r.srodek ?? r.od ?? r.do))

/** Rzut z projektu albo null, gdy projekt jest za pusty (bez budynku) albo uszkodzony. */
export const rzutZProjektu = (projekt) => {
  if (!projekt || typeof projekt !== 'object' || !projekt.budynek) return null
  try { return daneRzutu(projekt) } catch { return null }
}

/**
 * Arkusze całej teczki w jednym PDF (P5 + P6): rzut dachu i arkusze połaci, schemat instalacji i rozmieszczenie
 * urządzeń, z wspólną numeracją „ark. i z n”. Z samego widoku wykonawcy (projekt i `miejsca` bez adresu i mapy).
 * Łańcuchów Projekt 3D nie liczy, więc schemat mówi, że podział na łańcuchy dobiera wykonawca. `null` bez projektu.
 */
export const opisRysunkowTeczki = (projekt, miejsca = null, { format, nrTeczki = '' } = {}) => {
  if (!rzutZProjektu(projekt)) return null
  const dane = daneInstalacjiZProjektu(projekt, { miejsca })
  const dach = opisRysunkuDachu(dane.rzut, { format })
  const instalacja = opisRysunkowInstalacji(dane, { format })
  const arkusze = ponumerujArkusze([...dach.arkusze, ...instalacja.arkusze], { nrTeczki })
  return { tytul: 'Teczka instalacji: rysunki', format: dach.format, arkusze }
}

/**
 * Podsumowanie projektu dla wykonawcy: ile modułów i jakiej mocy, na jakich połaciach, jaki sprzęt, z jakiej okolicy.
 * `null`, gdy teczka nie ma jeszcze projektu.
 */
export const podsumowanieProjektu = (projekt, rzut = rzutZProjektu(projekt)) => {
  if (!rzut) return null
  const lok = projekt.lokalizacja || {}
  const falownik = nazwaSprzetu(projekt.falownik?.model?.producent, projekt.falownik?.model?.nazwa)
  const pojemnosc = projekt.magazyn?.pojemnosc || 0
  const magazyn = pojemnosc > 0
    ? [nazwaSprzetu(projekt.magazyn?.model?.producent, projekt.magazyn?.model?.nazwa), `${String(pojemnosc).replace('.', ',')} kWh`]
      .filter(Boolean).join(', ')
    : ''
  return {
    rodzaj: rzut.rodzaj === 'grunt' ? 'grunt' : 'dach',
    liczba: rzut.liczba || 0,
    mocKwp: rzut.mocKwp || 0,
    modul: rzut.modul?.nazwa ? `${rzut.modul.nazwa}${String(rzut.modul.nazwa).includes(String(rzut.modul.moc)) ? '' : `, ${rzut.modul.moc} W`}` : '',
    falownik,
    magazyn,
    magazynKwh: pojemnosc,
    polacie: (rzut.polacie || []).filter((p) => !p.grunt).map((p) => ({
      litera: p.litera, nachylenie: p.nachylenie, kierunek: p.kierunek, plaska: p.plaska, liczba: p.moduly.length,
    })),
    miejscowosc: lok.miejscowosc || '',
    wojewodztwo: lok.wojewodztwo || '',
    bezMapy: Boolean(projekt.bezMapy),
  }
}

/**
 * „Produkcja i zwrot” z części `pieniadze` (kształt sesji pieniędzy, `podglad`): zakresy zapisane przy ostatnim
 * policzeniu. `null`, gdy klient jeszcze nie liczył opłacalności. Cena posiadanej oferty (`kosztZl`) nie dociera do
 * wykonawcy przed kontaktem, więc tu jej nie ma.
 */
export const opisOplacalnosci = (pieniadze) => {
  const p = pieniadze?.podglad
  if (!p || (!p.rok1Zl && !p.zwrotLata)) return null
  const bez = p.zwrotLata?.bezDotacji
  const z = p.zwrotLata?.zDotacja
  return {
    taryfa: p.taryfa || pieniadze.taryfa || null,
    data: p.data || null,
    rok: p.rok1Zl ? `${zakresPl(p.rok1Zl, zl)} zł` : null,
    lata25: p.lata25Zl ? `${zakresPl(p.lata25Zl, zl)} zł` : null,
    zwrot: bez ? `${zakresPl(bez, lata)} lat` : null,
    zwrotZDotacja: z ? `${zakresPl(z, lata)} lat` : null,
  }
}

/**
 * Szkic zlecenia z teczki konta prywatnego (`Rynek` czyta go z `state.ogloszenie`, jak zestaw z asystenta): typ, moc
 * i sprzęt z projektu oraz `teczka_id`, żeby zlecenie niosło migawkę teczki. Bez projektu `null`.
 */
export const ogloszenieZTeczki = (teczka) => {
  const projekt = teczka?.projekt
  const s = podsumowanieProjektu(projekt)
  if (!s || !teczka?.id) return null
  const opis = Object.fromEntries(Object.entries({
    kwp: s.mocKwp ? Math.round(s.mocKwp * 100) / 100 : null,
    liczba_modulow: s.liczba || null,
    moduly: s.modul || null,
    falownik: s.falownik || null,
    magazyn: s.magazyn || null,
    magazyn_kwh: s.magazynKwh || null,
  }).filter(([, v]) => v !== null && v !== ''))
  return { zrodlo: 'wprost', tryb: 'szczegolowe', typ: s.magazynKwh ? 'pv_magazyn' : 'pv', opis, teczka_id: teczka.id }
}
